/**
 * The app registry: everything the dock and window layer know about an app.
 * Apps are lazy (React.lazy per definition) so each becomes its own Vite chunk
 * and the shell stays tiny. Radar is the engine's staff workspace only (founder,
 * 2026-10-02), so every app here reads the engine, with the engine's permissions.
 */
import { lazy, type ComponentType, type LazyExoticComponent, type ReactNode } from "react";
import type { BadgeValue, QuickAction, QuickActionContext } from "../shell/mobile/types";

export interface RadarAppProps {
  windowId: string;
  launchParams?: Record<string, string> | undefined;
}

export interface RadarAppDefinition {
  id: string;
  name: string;
  icon: ReactNode;
  /** true → opening again focuses the existing window */
  singleton: boolean;
  /** Engine permission key required to SEE the app (null = every signed-in staff member).
      The owner sees everything; the engine re-checks on every call. */
  permission: string | null;
  /** Launchable but not shown in the dock/tab bar (e.g. Profile, opened from
      the menu-bar user icon). */
  hidden?: boolean;
  defaultBounds: { w: number; h: number };
  Component: LazyExoticComponent<ComponentType<RadarAppProps>>;

  // ---- read only by the phone presenter (docs/PLAN-2026-09-21-mobile-os-shell.md, the fields of the desk's
  // DeskAppDefinition); the desktop ignores them ----

  /** A shorter name for the phone's home grid, where a label has room for about ten letters. The window keeps `name`. */
  short?: string;
  /** Its place on the phone's home grid, lowest first. Absent: the App Library only. */
  primary?: number;
  /** Its group in the App Library. Absent: "Other". */
  category?: string;
  /** What a long press on its icon offers, besides Open. Offer only what this user can do now (set by shell/phone.tsx). */
  quickActions?: (ctx: QuickActionContext) => QuickAction[];
  /** Something new in it: a count, a dot (true), or nothing. Read on every render of the home screen, so keep it cheap. */
  badge?: () => BadgeValue;
}

/** The App Library's groups (founder's inspiration image, 2026-09-21), filled only with apps that exist. */
export const ENGINE_GROUPS = ["Engine", "Learning", "System"] as const;

const ic = (paths: string, viewBox = "0 0 24 24") => (
  <svg width="22" height="22" viewBox={viewBox} fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    {paths.split("|").map((d, i) => (
      <path key={i} d={d} />
    ))}
  </svg>
);

export const APPS: RadarAppDefinition[] = [
  // ---- The engine's staff apps (docs/PLAN-2026-09-17-admin-engine.md) ----
  {
    id: "engine-cockpit",
    primary: 1, short: "Cockpit", category: "Engine",
    name: "Engine Cockpit",
    icon: ic("M4 13a8 8 0 0 1 16 0|M12 13l3.5-4|M6.5 19.5h11|M4 13h2|M18 13h2"),
    singleton: true,
    permission: "engine.costs.view",
    defaultBounds: { w: 980, h: 700 },
    Component: lazy(() => import("./engine/Cockpit")),
  },
  {
    // docs/PLAN-2026-09-19-engine-blueprint.md: the living model of the engine.
    id: "engine-blueprint",
    category: "Engine",
    name: "Blueprint",
    icon: ic("M4 5.5h16v13H4z|M4 10h16|M9.5 10v8.5|M13 13.5h4M13 16h2.5"),
    singleton: true,
    permission: "engine.blueprint.view",
    defaultBounds: { w: 1280, h: 800 },
    Component: lazy(() => import("./engine/blueprint/Blueprint")),
  },
  {
    // Owner only: packages/engine/src/admin.ts OWNER_ONLY.
    id: "engine-analytics",
    category: "System",
    name: "Analytics",
    icon: ic("M4 20h16|M7.5 20v-6.5|M12 20V8.5|M16.5 20v-9.5|M3.5 5.5c2.5 0 3-2 5.5-2s3.5 3 6 3 2.5-1.5 5-1.5"),
    singleton: true,
    permission: "engine.analytics.view",
    defaultBounds: { w: 1040, h: 700 },
    Component: lazy(() => import("./engine/analytics/Analytics")),
  },
  {
    id: "engine-learners",
    primary: 2, category: "Learning",
    name: "Learners",
    icon: ic("M9 11.5a3.2 3.2 0 1 0 0-6.4 3.2 3.2 0 0 0 0 6.4z|M3.5 19.5c.7-3 2.8-4.7 5.5-4.7s4.8 1.7 5.5 4.7|M16 5.6a2.7 2.7 0 1 1 0 5.4|M17.5 14.9c1.7.5 2.7 1.7 3 3.6"),
    singleton: true,
    permission: "engine.learners.view",
    defaultBounds: { w: 1180, h: 740 },
    Component: lazy(() => import("./engine/Learners")),
  },
  {
    id: "engine-sessions",
    primary: 3, category: "Learning",
    name: "Sessions",
    icon: ic("M4 6.5A2.5 2.5 0 0 1 6.5 4h11A2.5 2.5 0 0 1 20 6.5v8a2.5 2.5 0 0 1-2.5 2.5H13l-4 3.6V17H6.5A2.5 2.5 0 0 1 4 14.5z|M8.5 9h7M8.5 12h4.5"),
    singleton: true,
    permission: "engine.sessions.view",
    defaultBounds: { w: 1180, h: 760 },
    Component: lazy(() => import("./engine/Sessions")),
  },
  {
    id: "engine-flags",
    primary: 4, category: "Engine",
    name: "Reports",
    icon: ic("M5 21V4.5|M5 4.5h11l-2.2 4 2.2 4H5"),
    singleton: true,
    permission: "engine.flags.review",
    defaultBounds: { w: 1040, h: 700 },
    Component: lazy(() => import("./engine/Flags")),
  },
  {
    // Questions (founder, 2026-10-10): every row of the bank, live or not, those needing repair included, as a student
    // sees it. engine.bank.view to look (admin, owner and editor); Retire needs engine.bank.edit (admin and owner).
    id: "engine-questions",
    primary: 7, short: "Questions", category: "Engine",
    name: "Questions",
    icon: ic("M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18z|M9.6 9.3a2.5 2.5 0 1 1 3.4 2.3c-.6.3-1 .8-1 1.5v.4|M12 16.8h.01"),
    singleton: true,
    permission: "engine.bank.view",
    defaultBounds: { w: 1180, h: 780 },
    Component: lazy(() => import("./engine/Questions")),
  },
  {
    // Owner only (founder, 2026-09-20): the one place a school and its main admin are created.
    id: "engine-schools",
    primary: 5, category: "Learning",
    name: "Schools",
    icon: ic("M3 10l9-5 9 5-9 5-9-5z|M7 12.5V17c1.4 1.3 3.1 2 5 2s3.6-.7 5-2v-4.5|M21 10v5"),
    singleton: true,
    permission: "engine.orgs.manage",
    defaultBounds: { w: 1040, h: 700 },
    Component: lazy(() => import("./engine/Schools")),
  },
  {
    // Send feedback (founder, 2026-09-22): what students and school staff wrote to the GoGamo team. Never a school's.
    // "Student feedback", so it is never confused with Radar's own Feedback app (plan 2026-09-20 §4.2).
    id: "engine-feedback",
    primary: 6, short: "Feedback", category: "Learning",
    name: "Student feedback",
    icon: ic("M4 5.5h16v10H9l-4 3.5v-3.5H4z|M8 9h8M8 12h5"),
    singleton: true,
    permission: "engine.feedback.view",
    defaultBounds: { w: 1040, h: 700 },
    Component: lazy(() => import("./engine/Feedback")),
  },
  {
    // The Readiness lab (founder, 2026-09-22): readiness is staff only. How the estimate did against real scores,
    // proposed goal.yaml values (never applied), and one learner's readiness over time.
    id: "engine-readiness",
    category: "Learning",
    name: "Readiness lab",
    icon: ic("M4 19h16|M6 15.5l4-4 3 3 5-6.5|M18 8h-3|M18 8v3"),
    singleton: true,
    permission: "engine.readiness.view",
    defaultBounds: { w: 1120, h: 740 },
    Component: lazy(() => import("./engine/ReadinessLab")),
  },
  {
    id: "engine-ratings",
    category: "Engine",
    name: "Ratings",
    icon: ic("M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18z|M8.5 14.5c.9 1.2 2.1 1.8 3.5 1.8s2.6-.6 3.5-1.8|M9 9.5h.01|M15 9.5h.01"),
    singleton: true,
    permission: "engine.ratings.view",
    defaultBounds: { w: 1040, h: 700 },
    Component: lazy(() => import("./engine/Ratings")),
  },
  {
    id: "engine-staff",
    short: "Staff", category: "System",
    name: "Staff",
    icon: ic("M9 11.5a3.2 3.2 0 1 0 0-6.4 3.2 3.2 0 0 0 0 6.4z|M3.5 19.5c.7-3 2.8-4.7 5.5-4.7s4.8 1.7 5.5 4.7|M16 5.6a2.7 2.7 0 1 1 0 5.4|M17.5 14.9c1.7.5 2.7 1.7 3 3.6"),
    singleton: true,
    permission: "engine.staff.manage",
    defaultBounds: { w: 640, h: 620 },
    Component: lazy(() => import("./engine/Staff")),
  },
  {
    id: "profile",
    name: "My profile",
    icon: ic("M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8z|M5 20c1-3.5 3.8-5.3 7-5.3s6 1.8 7 5.3"),
    singleton: true,
    permission: null,
    hidden: true,
    defaultBounds: { w: 460, h: 560 },
    Component: lazy(() => import("./profile/Profile")),
  },
  {
    id: "about",
    short: "About", category: "System",
    name: "About Radar",
    icon: ic("M12 3a9 9 0 1 0 9 9|M12 7v5l3.5 2|M21 3l-4.5 4.5", "0 0 24 24"),
    singleton: true,
    permission: null,
    defaultBounds: { w: 460, h: 400 },
    Component: lazy(() => import("./about/About")),
  },
];

export function appById(id: string): RadarAppDefinition | undefined {
  return APPS.find((a) => a.id === id);
}
